import { ld } from "@/lib/theme/light-dark";
import {
  CalendarClock,
  Check,
  ChevronLeft,
  ChevronRight,
  Plus,
  Settings2,
} from "lucide-react";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import { H, MODULE_HEIGHT, SIDEBAR_WIDTH } from "../../hero-tokens";
import type { ModuleProps } from "../module-registry";
import {
  type CalEvent,
  GRID_HEIGHT,
  HOURS,
  HOUR_H,
  MINI_WEEKS,
  type WeekDay,
  weekDays,
} from "./module-kalender-data";

const WEEKDAY_LETTERS = ["MO", "DI", "MI", "DO", "FR", "SA", "SO"] as const;

function KalenderSidebar(): ReactNode {
  const t = useTranslations("marketing.moduleKalender");
  return (
    <div
      style={{
        width: SIDEBAR_WIDTH,
        borderRight: `1px solid ${ld("#EEF0F3", "#2c2f33")}`,
        background: ld("#FBFBFC", "#1d1f23"),
        padding: "16px 12px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 4px 14px",
        }}
      >
        <span style={{ fontSize: 16, fontWeight: 700 }}>{t("kalender")}</span>
        <span
          style={{
            color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"),
            display: "inline-flex",
            cursor: "pointer",
          }}
        >
          <Settings2 width={15} height={15} strokeWidth={1.8} />
        </span>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 4px 9px",
        }}
      >
        <span style={{ fontSize: 13.5, fontWeight: 700, color: ld("#0B0F19", "#fafafa") }}>
          {t("juni2026")}
        </span>
        <span style={{ display: "flex", gap: 9, color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)") }}>
          <ChevronLeft
            width={15}
            height={15}
            strokeWidth={1.9}
            style={{ cursor: "pointer" }}
          />
          <ChevronRight
            width={15}
            height={15}
            strokeWidth={1.9}
            style={{ cursor: "pointer" }}
          />
        </span>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7,1fr)",
          textAlign: "center",
          fontFamily: H.label,
          fontSize: 9,
          letterSpacing: "0.03em",
          color: ld("rgba(11,15,25,0.34)", "rgba(250,250,250,0.34)"),
          textTransform: "uppercase",
          paddingBottom: 5,
        }}
      >
        {WEEKDAY_LETTERS.map((letter) => (
          <span key={letter}>{letter}</span>
        ))}
      </div>
      {MINI_WEEKS.map((wk, wi) => (
        <div
          key={wi}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7,1fr)",
            gap: 1,
            marginBottom: 1,
          }}
        >
          {wk.map((d, di) => (
            <span
              key={di}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 25,
                fontSize: 11.5,
                borderRadius: 6,
                background: d.bg,
                color: d.color,
                fontWeight: d.weight,
              }}
            >
              {d.day}
            </span>
          ))}
        </div>
      ))}
      <div style={{ height: 1, background: ld("#EEF0F3", "#2c2f33"), margin: "16px 2px" }} />
      <div
        style={{
          fontSize: 11.5,
          color: ld("rgba(11,15,25,0.45)", "rgba(250,250,250,0.45)"),
          padding: "0 2px 11px",
        }}
      >
        {t("k24KalendernAktiv")}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <CalendarToggle color={H.accent} label={t("meinKalender")} checked />
        <CalendarToggle color={ld("#3F7E91", "#77b6ca")} label={t("teamTermine")} checked />
        <CalendarToggle label={t("objekttermine")} />
        <CalendarToggle label={t("feiertageDe")} />
      </div>
    </div>
  );
}

function CalendarToggle({
  color,
  label,
  checked,
}: {
  readonly color?: string;
  readonly label: string;
  readonly checked?: boolean;
}): ReactNode {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "5px 6px",
        borderRadius: 7,
      }}
    >
      {checked && color ? (
        <span
          style={{
            width: 15,
            height: 15,
            borderRadius: 4,
            background: color,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: ld("#fff", "#181b1f"),
            flexShrink: 0,
          }}
        >
          <Check width={11} height={11} strokeWidth={3} />
        </span>
      ) : (
        <span
          style={{
            width: 15,
            height: 15,
            borderRadius: 4,
            border: `1.5px solid ${ld("#CFD3DA", "#45494d")}`,
            boxSizing: "border-box",
            flexShrink: 0,
          }}
        />
      )}
      <span
        style={{
          fontSize: 13,
          color: checked ? ld("rgba(11,15,25,0.8)", "rgba(250,250,250,0.8)") : ld("rgba(11,15,25,0.5)", "rgba(250,250,250,0.5)"),
        }}
      >
        {label}
      </span>
    </div>
  );
}

function WeekHeaderCell({ d }: { readonly d: WeekDay }): ReactNode {
  return (
    <div
      style={{
        textAlign: "center",
        padding: "7px 0 9px",
        borderLeft: `1px solid ${ld("#F1F2F4", "#2b2e32")}`,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: d.dayColor,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {d.letter}
      </div>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 28,
          height: 28,
          borderRadius: "50%",
          marginTop: 4,
          fontSize: 15,
          fontWeight: 700,
          background: d.dateBg,
          color: d.dateColor,
        }}
      >
        {d.date}
      </div>
    </div>
  );
}

function EventBlock({ e }: { readonly e: CalEvent }): ReactNode {
  return (
    <div
      style={{
        position: "absolute",
        top: e.top,
        height: e.height,
        left: 3,
        right: 3,
        background: e.bg,
        color: e.fg,
        borderLeft: `3px solid ${e.bar}`,
        borderRadius: 5,
        padding: "4px 7px",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 600,
          lineHeight: 1.2,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {e.title}
      </div>
      <div style={{ fontSize: 10, opacity: 0.78, marginTop: 1 }}>{e.time}</div>
    </div>
  );
}

function WeekColumn({ d }: { readonly d: WeekDay }): ReactNode {
  return (
    <div
      style={{
        position: "relative",
        borderLeft: `1px solid ${ld("#F1F2F4", "#2b2e32")}`,
        height: GRID_HEIGHT,
        backgroundColor: d.colBg,
        backgroundImage: `repeating-linear-gradient(${ld("rgba(11,15,25,0.05)", "rgba(250,250,250,0.05)")} 0 1px, transparent 1px ${HOUR_H}px)`,
      }}
    >
      {d.events.map((e, ei) => (
        <EventBlock key={ei} e={e} />
      ))}
    </div>
  );
}

function KalenderContent({
  setActive,
}: { readonly setActive: ModuleProps["setActive"] }): ReactNode {
  const tModuleKalenderData = useTranslations("marketing.moduleKalenderData");
  const t = useTranslations("marketing.moduleKalender");
  return (
    <div
      style={{
        margin: "-20px -22px",
        height: MODULE_HEIGHT,
        display: "flex",
        flexDirection: "column",
        background: ld("#fff", "#181b1f"),
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "11px 18px",
          borderBottom: `1px solid ${ld("#F1F2F4", "#2b2e32")}`,
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
          <span
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: ld("rgba(11,15,25,0.5)", "rgba(250,250,250,0.5)"),
              cursor: "pointer",
            }}
          >
            <ChevronLeft width={18} height={18} strokeWidth={1.9} />
          </span>
          <span
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: ld("rgba(11,15,25,0.5)", "rgba(250,250,250,0.5)"),
              cursor: "pointer",
            }}
          >
            <ChevronRight width={18} height={18} strokeWidth={1.9} />
          </span>
        </div>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            height: 32,
            padding: "0 13px",
            border: `1px solid ${ld("#E7E9ED", "#32363a")}`,
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            color: ld("#0B0F19", "#fafafa"),
            cursor: "pointer",
          }}
        >
          {t("heute")}
        </span>
        <span style={{ fontSize: 15, fontWeight: 700, color: ld("#0B0F19", "#fafafa") }}>
          {t("k2228Juni2026")}
        </span>
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span
            onClick={() => setActive("terminbuchung")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              height: 32,
              padding: "0 13px",
              border: `1px solid ${ld("#E7E9ED", "#32363a")}`,
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              color: ld("#0B0F19", "#fafafa"),
              cursor: "pointer",
            }}
          >
            <CalendarClock width={15} height={15} strokeWidth={1.8} />
            {t("terminbuchung")}
          </span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              height: 32,
              padding: "0 13px",
              borderRadius: 8,
              background: H.accent,
              color: ld("#fff", "#181b1f"),
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <Plus width={15} height={15} strokeWidth={2.2} />
            {t("termin")}
          </span>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              background: ld("#F4F5F7", "#272a2e"),
              border: `1px solid ${ld("#ECEEF1", "#2e3135")}`,
              borderRadius: 8,
              padding: 2,
              fontSize: 12.5,
              fontWeight: 600,
            }}
          >
            <span
              style={{
                padding: "5px 10px",
                borderRadius: 6,
                color: ld("rgba(11,15,25,0.5)", "rgba(250,250,250,0.5)"),
                cursor: "pointer",
              }}
            >
              {t("tag")}
            </span>
            <span
              style={{
                padding: "5px 10px",
                borderRadius: 6,
                background: ld("#fff", "#181b1f"),
                color: ld("#0B0F19", "#fafafa"),
                boxShadow: `0 1px 2px ${ld("rgba(11,15,25,0.07)", "rgba(0,0,0,0.155)")}`,
              }}
            >
              {t("woche")}
            </span>
            <span
              style={{
                padding: "5px 10px",
                borderRadius: 6,
                color: ld("rgba(11,15,25,0.5)", "rgba(250,250,250,0.5)"),
                cursor: "pointer",
              }}
            >
              {t("monat")}
            </span>
          </div>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexShrink: 0,
          borderBottom: `1px solid ${ld("#F1F2F4", "#2b2e32")}`,
        }}
      >
        <div style={{ width: 54, flexShrink: 0 }} />
        <div
          style={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: "repeat(7,1fr)",
          }}
        >
          {weekDays(tModuleKalenderData).map((d) => (
            <WeekHeaderCell key={d.date} d={d} />
          ))}
        </div>
      </div>
      <div style={{ flex: 1, overflow: "hidden", display: "flex" }}>
        <div style={{ width: 54, flexShrink: 0, paddingTop: 0 }}>
          {HOURS.map((h) => (
            <div
              key={h}
              style={{
                height: HOUR_H,
                textAlign: "right",
                paddingRight: 9,
                fontFamily: H.label,
                fontSize: 10,
                color: ld("rgba(11,15,25,0.38)", "rgba(250,250,250,0.38)"),
                transform: "translateY(-6px)",
              }}
            >
              {h}
            </div>
          ))}
        </div>
        <div
          style={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: "repeat(7,1fr)",
          }}
        >
          {weekDays(tModuleKalenderData).map((d) => (
            <WeekColumn key={d.date} d={d} />
          ))}
        </div>
      </div>
    </div>
  );
}

export function ModuleKalender(props: ModuleProps): ReactNode {
  return (
    <div style={{ display: "flex", height: MODULE_HEIGHT }}>
      <KalenderSidebar />
      <div
        style={{
          flex: 1,
          minWidth: 0,
          background: ld("#fff", "#181b1f"),
          padding: "20px 22px",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <KalenderContent setActive={props.setActive} />
      </div>
    </div>
  );
}
